/**
 * @description Persists explicit cookie consent preferences for the public site.
 * @filename cookie-consent-storage.ts
 */
import { storageFactory } from "./local-storage-factory";

const localStore = storageFactory(() => localStorage);

export type CookieConsentPreferences = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
};

/** @deprecated Binary choice kept for migration from PR #433 storage. */
export type CookieConsentChoice = "accepted" | "rejected";

const CONSENT_KEY = "rdvCookieConsent";
const LEGACY_CONSENT_KEY = "cookieConsent";

export function createDefaultPreferences(
  analytics: boolean,
  marketing: boolean
): CookieConsentPreferences {
  return {
    essential: true,
    analytics,
    marketing,
  };
}

export function acceptAllPreferences(): CookieConsentPreferences {
  return createDefaultPreferences(true, true);
}

export function rejectAllPreferences(): CookieConsentPreferences {
  return createDefaultPreferences(false, false);
}

function parsePreferences(raw: string | null): CookieConsentPreferences | null {
  if (!raw) {
    return null;
  }

  if (raw === "accepted") {
    return acceptAllPreferences();
  }

  if (raw === "rejected") {
    return rejectAllPreferences();
  }

  try {
    const parsed = JSON.parse(raw) as Partial<CookieConsentPreferences>;
    if (typeof parsed.analytics === "boolean" && typeof parsed.marketing === "boolean") {
      return {
        essential: true,
        analytics: parsed.analytics,
        marketing: parsed.marketing,
      };
    }
  } catch {
    // Ignore invalid JSON and treat as undecided.
  }

  return null;
}

function readStoredConsent(): CookieConsentPreferences | null {
  for (const key of [CONSENT_KEY, LEGACY_CONSENT_KEY]) {
    const preferences = parsePreferences(localStore.getItem(key));
    if (preferences) {
      return preferences;
    }
  }
  return null;
}

export function getCookieConsentPreferences(): CookieConsentPreferences | null {
  return readStoredConsent();
}

/**
 * @deprecated Prefer getCookieConsentPreferences(). Maps preferences to binary for older callers.
 */
export function getCookieConsent(): CookieConsentChoice | null {
  const preferences = getCookieConsentPreferences();
  if (!preferences) {
    return null;
  }

  return preferences.analytics || preferences.marketing ? "accepted" : "rejected";
}

export function setCookieConsentPreferences(preferences: CookieConsentPreferences): void {
  localStore.setItem(
    CONSENT_KEY,
    JSON.stringify({
      essential: true,
      analytics: preferences.analytics,
      marketing: preferences.marketing,
    })
  );
  localStore.removeItem(LEGACY_CONSENT_KEY);
  syncCookieConsentDocumentAttribute();
}

/**
 * @deprecated Prefer setCookieConsentPreferences().
 */
export function setCookieConsent(choice: CookieConsentChoice): void {
  setCookieConsentPreferences(choice === "accepted" ? acceptAllPreferences() : rejectAllPreferences());
}

export function hasCookieConsentDecision(): boolean {
  return getCookieConsentPreferences() !== null;
}

export function syncCookieConsentDocumentAttribute(): void {
  if (typeof document === "undefined") {
    return;
  }

  if (hasCookieConsentDecision()) {
    document.documentElement.setAttribute("data-cookie-consent-resolved", "true");
    return;
  }

  document.documentElement.removeAttribute("data-cookie-consent-resolved");
}
